import React, { createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AvailablePlatform } from "../../../../providers/Auth/platform.provider";
import { IAddressLean } from "../../../domain/address/address-lean";
import { UserConverter } from "../../../domain/converter/user.converter";
import { IGroup } from "../../../domain/group/group";
import { cfmGroupTypes, GroupType, pomGroupTypes } from "../../../domain/group/group-type";
import { isAdmin, isPomMainContact, IUser } from "../../../domain/user/user";
import { useGetGroupAddresses } from "../../../repositories/queries/address/get-group-addresses.query";
import { useGetGroupsWithFiltersQuery } from "../../../repositories/queries/group/get-groups.query";
import { useCreateUserQuery } from "../../../repositories/queries/user/mutation/create-user.query";
import { getSelectAllAddressesOption } from "../../address/address-form/address-form.util";
import { IMutateUserFormInputs } from "./create-user-form.component";
import {
  applyCreateUserPlatformDefaults,
  assignableAddressTypes,
  isRestrictedToWasteProducer,
} from "./create-user-form.helper";

interface ICreateUserFormContext {
  groups: IGroup[];
  userGroupId?: number | undefined;
  userAddresses?: IAddressLean[];
  isRestrictedToWasteProducer: boolean;
  isPomMainContact: boolean;
  groupAddresses: IAddressLean[];
  isAdmin: boolean;
  isLoading: boolean;
  handleFormSubmit: (inputs: IMutateUserFormInputs) => Promise<boolean>;
}

export const CreateUserFormContext = createContext<ICreateUserFormContext>({} as ICreateUserFormContext);

export const useCreateUserFormContext = () => {
  return useContext(CreateUserFormContext);
};

const useCreateUserFormProvider = ({
  user,
  platformType,
  userAddresses,
}: ICreateUserFormProps): ICreateUserFormContext => {
  const { t } = useTranslation();

  const getGroupTypesToFetch = (): GroupType[] => {
    if (platformType === AvailablePlatform.Cfm) {
      return cfmGroupTypes;
    } else if (platformType === AvailablePlatform.Pom) {
      return pomGroupTypes;
    }
    return [];
  };

  const { isLoading: areAddressesLoading, data: addressesResult } = useGetGroupAddresses(
    user?.group?.id,
    undefined,
    assignableAddressTypes,
    !!user?.group?.id && !isAdmin(user),
    undefined,
    true,
  );

  const { data: groups, isLoading: areGroupsLoading } = useGetGroupsWithFiltersQuery(
    { filter: { groupTypes: getGroupTypesToFetch() } },
    isAdmin(user),
  );

  const { mutateAsync: createUser, isPending: isCreateUserLoading } = useCreateUserQuery();

  const isLoading = areAddressesLoading || areGroupsLoading || isCreateUserLoading;

  const isUserRestrictedToWasteProducer: boolean = isRestrictedToWasteProducer(user, platformType);
  const isUserPomMainContact: boolean = (isPomMainContact(user) && platformType === AvailablePlatform.Pom) ?? false;
  const isUserAdmin: boolean = isAdmin(user);

  const handleFormSubmit = async (inputs: IMutateUserFormInputs): Promise<boolean> => {
    // remove the all addresses option from input
    const filteredInputs = { ...inputs };
    filteredInputs.userAddresses = inputs.userAddresses?.filter((a) => a.id !== getSelectAllAddressesOption(t).id);

    let groupIdForUser = filteredInputs.group.id;
    if (user && !isAdmin(user) && user.group) {
      groupIdForUser = user.group.id;
    }

    const inputsWithDefaults = applyCreateUserPlatformDefaults(filteredInputs, platformType, isUserAdmin);
    const request = UserConverter.toMutationRequest(inputsWithDefaults, groupIdForUser);
    try {
      const response = await createUser({ createData: request });
      return !!response;
    } catch {
      // the error is shown by useCreateUserQuery and the global mutation error handler
      return false;
    }
  };

  // memoized, because the form resets the CFM user type whenever the addresses change
  const groupAddresses = useMemo((): IAddressLean[] => {
    // Waste producers may only assign their own addresses, even if they have none
    if (isUserRestrictedToWasteProducer) {
      return userAddresses ?? [];
    }

    // If userAddresses are set, only allow those for selections
    if (userAddresses && userAddresses?.length > 0) {
      return userAddresses;
    }

    // Otherwise use all addresses for group
    const addresses = addressesResult?.addresses;

    // Add all addresses option without changing the cached query result
    return addresses ? [...addresses, getSelectAllAddressesOption(t)] : [];
  }, [isUserRestrictedToWasteProducer, userAddresses, addressesResult, t]);

  return {
    groups: groups?.groups ?? [],
    isLoading,
    handleFormSubmit,
    isRestrictedToWasteProducer: isUserRestrictedToWasteProducer,
    isPomMainContact: isUserPomMainContact,
    isAdmin: isUserAdmin,
    groupAddresses,
  };
};

interface ICreateUserFormProps {
  user?: IUser | undefined;
  userAddresses?: IAddressLean[];
  platformType: AvailablePlatform;
  children?: React.ReactNode;
}

export const CreateUserFormProvider: React.FC<ICreateUserFormProps> = (props) => {
  const value = useCreateUserFormProvider(props);
  return <CreateUserFormContext.Provider value={value}>{props.children}</CreateUserFormContext.Provider>;
};
